import { useState } from "react";
import { Cpu, ChevronDown } from "lucide-react";

/**
 * Mission Watch — owner-only "Scan diagnostics" collapsible.
 *
 * Renders the `diagnostic` breadcrumb that the deep-scan crawler attaches to
 * the `donation_pages_watched` check on every stored report. This block is
 * invisible to normal customers — it's a debugging aid for the platform
 * owner when a customer asks "why is my count what it is?"
 *
 * The data is stored regardless of who's looking; this component just
 * decides whether to surface it. Gate at the call site with `user?.is_owner`.
 */
export function ScanDiagnostics({ diagnostic }) {
  const [open, setOpen] = useState(false);
  if (!diagnostic || typeof diagnostic !== "object") return null;

  // Ordered fields — most-useful-at-a-glance first.
  const rows = [
    ["Sitemap URLs discovered", diagnostic.sitemap_urls_discovered],
    ["All discovered URLs", diagnostic.all_discovered_urls],
    ["Pages visited", diagnostic.pages_visited],
    ["Pages OK", diagnostic.pages_ok],
    ["Pages broken", diagnostic.pages_broken],
    ["Pages behind bot-wall", diagnostic.pages_challenged],
    ["Donation pages from enumeration", diagnostic.donation_pages_from_enumeration],
    ["Donation pages total", diagnostic.donation_pages_total],
  ];

  const pagPrefixes = diagnostic.pagination_prefixes_synthesized || {};
  const pagKeys = Object.keys(pagPrefixes);

  return (
    <div className="mt-3 rounded-lg border border-indigo-200 bg-indigo-50/60" data-testid="scan-diagnostics">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left"
        data-testid="scan-diagnostics-toggle"
        aria-expanded={open}
      >
        <span className="inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.14em] text-indigo-700">
          <Cpu className="w-3.5 h-3.5" />
          Owner diagnostic
        </span>
        <ChevronDown className={`w-3.5 h-3.5 text-indigo-500 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-3 pb-3 pt-1 space-y-2" data-testid="scan-diagnostics-content">
          <p className="text-[11px] text-indigo-900/70 leading-relaxed">
            How this scan discovered its donation pages. Only visible to the platform owner.
          </p>
          <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-[11px]">
            {rows.map(([label, value]) => (
              <div key={label} className="flex items-center justify-between gap-2 border-b border-indigo-200/60 py-0.5">
                <dt className="text-charcoal-600">{label}</dt>
                <dd className="font-mono font-semibold text-indigo-800 tabular-nums">
                  {value === undefined || value === null ? "—" : value}
                </dd>
              </div>
            ))}
          </dl>
          {pagKeys.length > 0 && (
            <div>
              <p className="text-[10px] font-mono uppercase tracking-[0.14em] text-indigo-700 mb-1">
                Pagination synthesized
              </p>
              <ul className="space-y-0.5">
                {pagKeys.map((prefix) => (
                  <li key={prefix} className="text-[11px] font-mono text-charcoal-700">
                    <span className="text-indigo-700">{prefix}</span>
                    <span className="text-charcoal-400"> · pages </span>
                    {(pagPrefixes[prefix] || []).join(", ") || "—"}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
